import React, { useEffect, useState } from 'react';
import Footer from './common/home/Footer';
import TopBar from './common/home/TopBar';
import {
  Button,
  Grid,
  Icon,
  TextField,
  Typography
} from '@mui/material';
import { homeBackground } from './common/ColorCodes';
import DebugService from '../services/DebugService';
import SnackbarNotification, { SNACKBAR_THEME } from './common/SnackbarNotification';
import { IoIosSend } from "react-icons/io";
import { FaFilePdf } from "react-icons/fa";
import { BiUpload } from "react-icons/bi";
import ChatPDFApiService from '../services/ChatPDFApiService';
import { LoadingButton } from '@mui/lab';
import { CONST_SITE_LABEL, removeScopedItem, STORAGE_KEYS } from './common/Helper';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';

export const ChatPDF = () => {

  const [snackbarMessage, setSnackbarMessage] = useState(null)
  const [isErrorSnackbar, setIsErrorSnackbar] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [uploadedFileID, setUploadedFileID] = useState(null)
  const [uploadedFileName, setUploadedFileName] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [chatData, setChatData] = useState([])

  const { setScopedItem, getScopedItem } = useScopedLocalStorage()

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | Chat with PDF Upload`

    const uploadedFileFromHome = getScopedItem(STORAGE_KEYS.CHAT_PDF_FILE)
    if (uploadedFileFromHome) {
      const byteString = atob(uploadedFileFromHome.split(',')[1]);
      const mimeString = uploadedFileFromHome.split(',')[0].split(':')[1].split(';')[0];
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);

      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }

      let filename = `${(new Date()).getTime()}.pdf`

      if (getScopedItem(STORAGE_KEYS.CHAT_PDF_FILENAME)) {
        filename = getScopedItem(STORAGE_KEYS.CHAT_PDF_FILENAME)
      }

      const file = new File([uint8Array], filename, { type: mimeString });

      processUploadedFile(file)
    }
  }, [])

  const processUploadedFile = (file) => {
    reset()
    setIsErrorSnackbar(false)
    setSnackbarMessage(null)
    setIsLoading(true)

    if (!file) {
      DebugService.error(`ChatPDF Component: Uploaded file not found`, file)
      setSnackbarMessage('Uploaded file not found. Please try later')
      setIsErrorSnackbar(true)
      setIsLoading(false)
      return
    }

    const requestBody = {
      file
    }

    if (requestBody.file['type'] !== 'application/pdf') {
      DebugService.error(`ChatPDF Component: PDF type file is not uploaded`, requestBody)
      setSnackbarMessage('Please upload a PDF file')
      setIsErrorSnackbar(true)
      setIsLoading(false)
      return
    }

    const formData = new FormData()
    formData.append('file', requestBody['file'])

    DebugService.log(`ChatPDF Component: Upload PDF initiated`, requestBody)

    ChatPDFApiService.uploadPDF(formData)
      .then(res => {
        DebugService.log(`ChatPDF Component: Upload PDF response`, res, requestBody)
        if (res['status']) {

          if (['success', 'Processing in background'].includes(res['status'])) {
            setUploadedFileID(res['file_id'] ?? null)
            setUploadedFileName(requestBody['file']['name'])
            // Show success notification
            setSnackbarMessage('File uploaded successfully. You can start chat now.')
            setIsErrorSnackbar(false)
          } else {
            setSnackbarMessage(res['status'])
            setIsErrorSnackbar(false)
          }
        }

        setIsLoading(false)
      })
      .catch(error => {
        DebugService.error(`ChatPDF Component: Couldn't upload file`, error, requestBody)
        setSnackbarMessage('Couldn\'t upload file. Please try later')
        setIsErrorSnackbar(true)
        setIsLoading(false)

        if (getScopedItem(STORAGE_KEYS.CHAT_PDF_FILE)) {
          removeScopedItem(STORAGE_KEYS.CHAT_PDF_FILE)
        }
      })
  }

  /**
   * File upload submit handler
   * @param {*} event 
   * @returns 
   */
  const fileUploadHandler = (event) => {
    if (!event.target.files || !event.target.files.length) {
      DebugService.error(`ChatPDF Component: Uploaded file not found`, event.target)
      setSnackbarMessage('Uploaded file not found. Please try later')
      setIsErrorSnackbar(true)
      setIsLoading(false)
      return
    }

    processUploadedFile(event.target.files[0])
  }

  /**
   * Question submit handler
   */
  const sendBtnClickHandler = () => {
    setIsLoading(true)
    DebugService.log(`ChatPDF Component: Question is posted`, inputValue)

    const requestBody = {
      file_id: uploadedFileID,
      question: inputValue
    }

    DebugService.log(`ChatPDF Component: Question request is initiated`, requestBody)

    ChatPDFApiService.postQuestion(requestBody)
      .then(res => {
        DebugService.log(`ChatPDF Component: Question response`, res, requestBody)
        if (res['response']) {
          const newChat = {
            question: inputValue,
            answer: res['response']
          }

          setChatData(prev => [
            ...prev,
            newChat
          ])

          setInputValue('')
        }

        setIsLoading(false)
      })
      .catch(error => {
        DebugService.error(`ChatPDF Component: Couldn't fetch details for input question`, error, requestBody)
        setSnackbarMessage('Couldn\'t fetch details. Please try later')
        setIsErrorSnackbar(true)
        setIsLoading(false)
      })
  }

  const reset = () => {
    setInputValue('')
    setChatData([])
    setUploadedFileID(null)
    setUploadedFileName(null)
  }

  return (
    <div style={{ backgroundColor: homeBackground }}>
      <TopBar />

      <Grid container className='chatpdf-container'>
        <Grid item xs={12} className='chatpdf-title' style={{
          padding: '0'
        }}>
          <Typography variant={'h5'}>Chat with PDF Upload</Typography>
        </Grid>

        {/* File upload */}
        <Grid item xs={12} className='chatpdf-browse' style={{
          padding: '0'
        }}>
          {uploadedFileID !== null && (
            <Grid item container xs={12} className='uploaded-file-container'>
              <Grid item xs={10}>
                <FaFilePdf />
                {' '}
                {uploadedFileName}
              </Grid>
              <Grid item xs={2} style={{
                textAlign: 'end',
                cursor: 'pointer'
              }}>
                <Icon title="Re-upload" onClick={() => reset()}><BiUpload /></Icon>
              </Grid>
            </Grid>
          )}

          {uploadedFileID === null && (
            <TextField
              fullWidth
              type='file'
              accept="application/pdf"
              id="outlined-basic"
              variant="outlined"
              onChange={(e) => fileUploadHandler(e)}
            />
          )}
        </Grid>

        <Grid item xs={12} className='chatpdf-chatbox' style={{
          padding: '0'
        }}>
          {/* Chat data */}
          <Grid item xs={12} className='chat'>
            {chatData.map(chat => (
              <div className='chat-row'>
                <Typography className='chat-question'>
                  <span className='chat-question-span'>{chat['question']}</span>
                </Typography>
                <Typography className='chat-answer'>{chat['answer']}</Typography>
              </div>
            ))}
          </Grid>

          {/* Question box */}
          <Grid item xs={12} style={{
            alignContent: 'flex-end'
          }}>
            <div className="chatpdf-input">
              <input
                type="text"
                placeholder="Type your question..."
                value={inputValue}
                disabled={uploadedFileID === null}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyUp={(e) => {
                  if (e?.key?.toLocaleLowerCase() === 'enter' && uploadedFileID !== null) {
                    console.log('Enter key is pressed up', e)
                    sendBtnClickHandler()
                  }
                }}
              />
              <LoadingButton
                loading={isLoading}
                variant="outlined"
                style={{ background: '#20a7db' }}
                disabled={uploadedFileID === null || !inputValue}
                onClick={() => sendBtnClickHandler()}
                loadingPosition='end'
                endIcon={<IoIosSend />}
                >
              </LoadingButton>
            </div>
          </Grid>
        </Grid>
      </Grid>

      {/* Snackbar Notification */}
      {snackbarMessage && (
        <SnackbarNotification
          initialOpen={true}
          duration={7000}
          message={snackbarMessage}
          actionButtonName={'Ok'}
          theme={isErrorSnackbar ? SNACKBAR_THEME.RED : SNACKBAR_THEME.GREEN}
          yPosition={'top'}
          xPosition={'center'}
        />
      )}

      <Footer />
    </div>
  )
}
